/**
 * Transaction Detail Modal Component
 * Displays detailed transaction information
 *
 * @author SAUBHAGYA Development Team
 * @since Story 6-5
 *
 * @description
 * Modal component showing:
 * - Header with batch code and status badge
 * - Overview tab: basic transaction info (code, quantity, grade, material type, gaushala, dates)
 * - QC Details tab: QC information (if available)
 * - Payment Info tab: payment and approval details (if available)
 * - Tabs: Overview | QC Details | Payment Info
 *
 * @example
 * ```tsx
 * const [transactionId, setTransactionId] = useState<number | null>(null);
 *
 * <TransactionDetailModal
 *   open={transactionId !== null}
 *   onOpenChange={(open) => !open && setTransactionId(null)}
 *   transactionId={transactionId}
 * />
 * ```
 */

import React, { useState } from 'react';
import { format } from 'date-fns';
import { X, Package, Calendar, Hash, Building2, Leaf, AlertCircle, CreditCard, CheckCircle, Clock } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';

import { useTransaction } from '@/hooks/useTransactions';
import { TransactionStatusBadge, TransactionStatus } from '../badges/TransactionStatusBadge';
import { QCGradeBadge } from '../badges/QCGradeBadge';
import { TransactionDTO } from '@/services/manufacturing/manufacturingApi';

interface TransactionDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transactionId: number | null;
}

/**
 * Format date time
 */
function formatDateTime(dateString: string): string {
  try {
    return format(new Date(dateString), 'PPp');
  } catch {
    return dateString;
  }
}

/**
 * Format date only
 */
function formatDate(dateString: string): string {
  try {
    return format(new Date(dateString), 'PP');
  } catch {
    return dateString;
  }
}

/**
 * Loading skeleton for modal content
 */
function TransactionDetailSkeleton() {
  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
      </div>
      <Skeleton className="h-48 w-full" />
    </div>
  );
}

/**
 * Transaction Detail Modal Component
 */
export function TransactionDetailModal({
  open,
  onOpenChange,
  transactionId,
}: TransactionDetailModalProps) {
  const { data: transaction, isLoading, error } = useTransaction(
    transactionId || 0,
    open && transactionId !== null
  );

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">
            {transaction?.batchCode || 'Transaction Details'}
          </DialogTitle>
          <DialogDescription>
            {transaction ? `Created on ${formatDate(transaction.createdAt)}` : 'Loading transaction details...'}
          </DialogDescription>
        </DialogHeader>
        {transaction && (
          <div className="flex items-center gap-2 mt-2">
            <TransactionStatusBadge
              status={transaction.status as TransactionStatus}
              size="sm"
            />
          </div>
        )}

        {isLoading ? (
          <TransactionDetailSkeleton />
        ) : error ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>Failed to load transaction details. Please try again.</AlertDescription>
          </Alert>
        ) : !transaction ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>Transaction not found.</AlertDescription>
          </Alert>
        ) : (
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="qc">QC Details</TabsTrigger>
              <TabsTrigger value="payment">Payment Info</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Basic Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    {/* Batch Code */}
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Batch Code</label>
                      <p className="text-sm font-mono font-semibold mt-1">{transaction.batchCode}</p>
                    </div>

                    {/* Material Type */}
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Material Type</label>
                      <p className="text-sm font-medium mt-1">
                        {typeof transaction.rawMaterialType === 'string'
                          ? transaction.rawMaterialType
                          : (transaction.rawMaterialType as any)?.name
                          ? (transaction.rawMaterialType as any).name
                          : transaction.rawMaterialTypeName || '—'}
                      </p>
                    </div>

                    {/* Quantity */}
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Quantity</label>
                      <p className="text-sm font-mono font-semibold mt-1">
                        {transaction.quantityKg.toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{' '}
                        KG
                      </p>
                    </div>

                    {/* Grade */}
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Grade</label>
                      <div className="mt-1">
                        {transaction.qc?.grade ? (
                          <QCGradeBadge grade={transaction.qc.grade as any} size="sm" />
                        ) : (
                          <span className="text-sm text-muted-foreground">—</span>
                        )}
                      </div>
                    </div>

                    {/* Gaushala */}
                    <div className="col-span-2">
                      <label className="text-sm font-medium text-muted-foreground">Gaushala</label>
                      <p className="text-sm font-medium mt-1">
                        {transaction.gaushala?.name || transaction.gaushalaName || '—'}
                      </p>
                    </div>

                    {/* Status */}
                    <div className="col-span-2">
                      <label className="text-sm font-medium text-muted-foreground">Status</label>
                      <div className="mt-1">
                        <TransactionStatusBadge
                          status={transaction.status as TransactionStatus}
                          size="sm"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Dates */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Dates</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">Created</span>
                    <span className="font-medium">{formatDateTime(transaction.createdAt)}</span>
                  </div>
                  {transaction.updatedAt && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">Last Updated</span>
                      <span className="font-medium">{formatDateTime(transaction.updatedAt)}</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Notes */}
              {transaction.notes && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Notes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                      {transaction.notes}
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* QC Details Tab */}
            <TabsContent value="qc" className="space-y-4">
              {transaction.qc ? (
                <>
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">QC Assessment</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-muted-foreground">Grade</label>
                          <div className="mt-1">
                            <QCGradeBadge grade={transaction.qc.grade as any} />
                          </div>
                        </div>

                        {transaction.qc.verifiedAt && (
                          <div>
                            <label className="text-sm font-medium text-muted-foreground">
                              Verified At
                            </label>
                            <p className="text-sm font-medium mt-1">
                              {formatDateTime(transaction.qc.verifiedAt)}
                            </p>
                          </div>
                        )}
                      </div>

                      {transaction.qc.remarks && (
                        <>
                          <Separator />
                          <div>
                            <label className="text-sm font-medium text-muted-foreground">Remarks</label>
                            <p className="text-sm text-gray-700 dark:text-gray-300 mt-2 whitespace-pre-wrap">
                              {transaction.qc.remarks}
                            </p>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </>
              ) : (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>QC verification not yet performed for this transaction.</AlertDescription>
                </Alert>
              )}
            </TabsContent>

            {/* Payment Info Tab */}
            <TabsContent value="payment" className="space-y-4">
              {transaction.status === 'APPROVED' && transaction.approvedAt ? (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Approval & Payment</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Approved At</label>
                        <p className="text-sm font-medium mt-1">{formatDateTime(transaction.approvedAt)}</p>
                      </div>

                      {transaction.approvedPricePerKg && (
                        <div>
                          <label className="text-sm font-medium text-muted-foreground">
                            Approved Price per KG
                          </label>
                          <p className="text-sm font-mono font-semibold mt-1">
                            INR {transaction.approvedPricePerKg.toLocaleString('en-IN', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </p>
                        </div>
                      )}

                      {transaction.approvedPricePerKg && (
                        <div>
                          <label className="text-sm font-medium text-muted-foreground">
                            Total Payment Amount
                          </label>
                          <p className="text-sm font-mono font-semibold mt-1 text-green-600 dark:text-green-400">
                            INR{' '}
                            {(transaction.quantityKg * transaction.approvedPricePerKg).toLocaleString(
                              'en-IN',
                              { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                            )}
                          </p>
                        </div>
                      )}
                    </div>

                    {transaction.approvalNotes && (
                      <>
                        <Separator />
                        <div>
                          <label className="text-sm font-medium text-muted-foreground">Approval Notes</label>
                          <p className="text-sm text-gray-700 dark:text-gray-300 mt-2 whitespace-pre-wrap">
                            {transaction.approvalNotes}
                          </p>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    {transaction.status === 'INITIALIZED'
                      ? 'This transaction needs to be submitted for QC verification.'
                      : transaction.status === 'QC_PENDING'
                        ? 'This transaction is pending QC verification.'
                        : transaction.status === 'QC_VERIFIED'
                          ? 'This transaction is awaiting approval.'
                          : 'No payment information available for this transaction.'}
                  </AlertDescription>
                </Alert>
              )}
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default TransactionDetailModal;
